// src/services/cuentasCorrientesService.js
/**
 * Cuentas Corrientes Service - Accounts Receivable (AR) Management
 * Handles API communication for customer credit sales tracking
 */

import axios from 'axios';
import { NODE_BACKEND_API_URL } from '../config';

const CUENTAS_API_URL = `${NODE_BACKEND_API_URL}/cuentas-corrientes`;

/**
 * Get authentication headers with JWT token
 */
const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
    };
};

/**
 * Get all cuentas corrientes (AR records) with filters
 * @param {Object} filters - Filter options
 * @param {string} filters.estado - Status filter (PENDIENTE, VENCIDO, PAGADO, etc.)
 * @param {string} filters.clienteDocumento - Customer document number filter
 * @param {string} filters.cliente - Customer name, razon social or CUIT search
 * @param {string} filters.numero - Invoice number search (last 5 digits)
 * @param {string} filters.dateFrom - Start date filter (YYYY-MM-DD)
 * @param {string} filters.dateTo - End date filter (YYYY-MM-DD)
 * @param {boolean} filters.onlyOverdue - Show only overdue accounts
 * @param {number} filters.page - Page number for pagination
 * @param {number} filters.pageSize - Number of records per page
 * @param {string} filters.sortBy - Field to sort by (fecha_emision, fecha_vencimiento, total, saldo_pendiente, cliente, numero, dias_vencido, default)
 * @param {string} filters.sortOrder - Sort direction (ASC or DESC)
 * @returns {Promise} Response with cuentas list and pagination
 */
export const getCuentasCorrientes = async (filters = {}) => {
    try {
        // Match API query parameters from documentation
        const params = {
            estado: filters.estado || undefined,
            clienteDocumento: filters.clienteDocumento || undefined,
            cliente: filters.cliente || undefined,
            numero: filters.numero || undefined,
            dateFrom: filters.dateFrom || undefined,
            dateTo: filters.dateTo || undefined,
            onlyOverdue: filters.onlyOverdue || undefined,
            sortBy: filters.sortBy || undefined,
            sortOrder: filters.sortOrder || undefined,
            page: filters.page || 1,
            limit: filters.pageSize || 20
        };
        
        // Remove undefined params
        Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);
        
        console.log('🔍 [CuentasCorrientes API] Sending request with params:', params);
        
        const response = await axios.get(CUENTAS_API_URL, {
            params,
            headers: getAuthHeaders()
        });
        
        console.log('✅ [CuentasCorrientes API] Raw backend response:', response.data);
        console.log('✅ [CuentasCorrientes API] Summary from backend:', response.data?.summary);
        console.log('✅ [CuentasCorrientes API] First record:', response.data?.data?.[0]);
        
        // Transform response to match component expectations
        const data = response.data;
        return {
            success: data.success,
            data: {
                cuentas: data.data || [],
                pagination: {
                    currentPage: data.pagination?.page || 1,
                    pageSize: data.pagination?.pageSize || 20,
                    totalCount: data.pagination?.total || 0,
                    totalPages: data.pagination?.totalPages || 1
                },
                summary: data.summary || {}
            }
        };
    } catch (error) {
        console.error('[CuentasCorrientes] Error fetching:', error);
        throw error;
    }
};

/**
 * Register a payment for an AR account
 * @param {number} cuentaId - Cuenta corriente ID
 * @param {Object} paymentData - Payment information
 * @param {number} paymentData.amount - Payment amount
 * @param {string} paymentData.metodo - Payment method
 * @param {string} paymentData.referencia - Payment reference (optional)
 * @returns {Promise} Response with new status and balance
 */
export const registerPayment = async (cuentaId, paymentData) => {
    try {
        // Match API payload from documentation
        const response = await axios.post(
            `${CUENTAS_API_URL}/${cuentaId}/payment`,
            {
                monto_pago: paymentData.amount,
                metodo_pago: paymentData.metodo || 'Efectivo',
                referencia_pago: paymentData.referencia || null
            },
            { headers: getAuthHeaders() }
        );
        
        return response.data;
    } catch (error) {
        console.error('[CuentasCorrientes] Error registering payment:', error);
        throw error;
    }
};

/**
 * Get customer balance summary
 * @param {string} clienteDocumento - Customer document number (optional)
 * @returns {Promise} Response with customer balance summary
 */
export const getCustomerSummary = async (clienteDocumento = null) => {
    try {
        const params = clienteDocumento ? { clienteDocumento } : {};
        
        const response = await axios.get(
            `${CUENTAS_API_URL}/summary/customers`,
            {
                params,
                headers: getAuthHeaders()
            }
        );
        
        return response.data;
    } catch (error) {
        console.error('[CuentasCorrientes] Error fetching summary:', error);
        throw error;
    }
};

/**
 * Cancel a cuenta corriente
 * @param {number} cuentaId - Cuenta corriente ID
 * @param {string} motivo - Cancellation reason
 * @returns {Promise} Response confirming cancellation
 */
export const cancelCuenta = async (cuentaId, motivo = '') => {
    try {
        const response = await axios.put(
            `${CUENTAS_API_URL}/${cuentaId}/cancel`,
            { motivo },
            { headers: getAuthHeaders() }
        );
        
        return response.data;
    } catch (error) {
        console.error('[CuentasCorrientes] Error cancelling:', error);
        throw error;
    }
};

/**
 * Create cuenta corriente for an invoice (called during invoice creation)
 * @param {Object} data - Invoice and customer data
 * @param {number} data.invoiceId - Invoice ID
 * @param {string} data.clienteDocumento - Customer document (CUIT/DNI)
 * @param {string} data.clienteNombre - Customer name
 * @param {number} data.montoTotal - Invoice total amount
 * @param {string} data.fechaVencimiento - Due date (YYYY-MM-DD)
 * @param {string} data.condicionesPago - Payment terms (NET15, NET30, etc.)
 * @returns {Promise} Response with cuenta corriente ID and due date
 */
export const createCuentaCorriente = async (data) => {
    try {
        // Match API payload from documentation
        const response = await axios.post(
            `${CUENTAS_API_URL}/create`,
            {
                invoice_id: data.invoiceId,
                cliente_documento: data.clienteDocumento,
                cliente_nombre: data.clienteNombre,
                monto_total: data.montoTotal,
                fecha_vencimiento: data.fechaVencimiento,
                condiciones_pago: data.condicionesPago || 'NET15'
            },
            { headers: getAuthHeaders() }
        );
        
        return response.data;
    } catch (error) {
        console.error('[CuentasCorrientes] Error creating:', error);
        throw error;
    }
};
